import { NetworkStatus } from '@apollo/client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UITable from 'UIComponents/table/UITable';
import EnrollmentsListErrorState from 'automation-ui-canvas/containers/enrollment-history/panel/enrollments-list/EnrollmentsListErrorState';
import { useSkippedEnrollmentsList } from 'automation-ui-canvas/containers/enrollment-history/panel/hooks/skippedEnrollmentHooks';
import { SKIPPED_ENROLLMENT_LIST_FIELDNAME } from 'automation-ui-canvas/containers/enrollment-history/panel/queries/skippedEnrollmentQueries';
import SkippedEnrollmentRow from 'automation-ui-canvas/containers/enrollment-history/panel/skipped-enrollments-list/SkippedEnrollmentRow';
import SkippedEnrollmentsEmptyState from 'automation-ui-canvas/containers/enrollment-history/panel/skipped-enrollments-list/SkippedEnrollmentsEmptyState';
import { transformSearchParamsForEnrollmentHistory, transformSearchParamsForTroubleshootEnrollment } from 'automation-ui-canvas/containers/enrollment-history/utils/dateFilterTransformers';
import NonEnrollmentDiagnosisPanelWrapper from 'automation-ui-management/components/panels/NonEnrollmentDiagnosisPanelWrapper';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { View } from 'foundations-components/transitional/components/View';
import { useHistory, useLocation } from 'react-router-dom';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SkippedEnrollmentsTable = ({
  skippedEnrollmentsList,
  openTroubleshootEnrollmentPanel
}) => {
  if (skippedEnrollmentsList.length === 0) {
    return /*#__PURE__*/_jsx(SkippedEnrollmentsEmptyState, {
      openTroubleshootEnrollmentPanel: openTroubleshootEnrollmentPanel
    });
  }
  return /*#__PURE__*/_jsxs(UITable, {
    children: [/*#__PURE__*/_jsxs("colgroup", {
      children: [/*#__PURE__*/_jsx("col", {
        style: {
          width: '70%'
        }
      }), /*#__PURE__*/_jsx("col", {
        style: {
          width: '30%'
        }
      })]
    }), /*#__PURE__*/_jsx("tbody", {
      children: skippedEnrollmentsList.map(skippedEnrollment => /*#__PURE__*/_jsx(SkippedEnrollmentRow, {
        skippedEnrollment: skippedEnrollment,
        openTroubleshootEnrollmentPanel: openTroubleshootEnrollmentPanel
      }, skippedEnrollment.idempotencyId))
    })]
  });
};
const SkippedEnrollmentsContent = ({
  flow,
  isHybrid
}) => {
  const history = useHistory();
  const location = useLocation();
  const trackEvent = useTrackEvent();
  const searchParams = new URLSearchParams(location.search);
  const isTroubleshootEnrollmentPanelOpen = searchParams.has('troubleshoot');
  const {
    data: {
      [SKIPPED_ENROLLMENT_LIST_FIELDNAME]: skippedEnrollmentsResponse = {}
    } = {},
    loading,
    networkStatus,
    fetchMore,
    error,
    refetch
  } = useSkippedEnrollmentsList({
    flowId: flow.flowId,
    objectTypeId: flow.objectTypeId,
    hybrid: !!isHybrid
  });
  if (loading && networkStatus !== NetworkStatus.fetchMore || searchParams.get('showVersions') === 'true' && !searchParams.get('version')) {
    return /*#__PURE__*/_jsx(UINanoProgress, {});
  }
  if (error) {
    return /*#__PURE__*/_jsx(EnrollmentsListErrorState, {
      onTryAgain: () => {
        refetch().catch(err => {
          reportError({
            error: err
          });
        });
      }
    });
  }
  const {
    results: skippedEnrollmentsList = [],
    hasMore: hasMoreSkippedEnrollments,
    offset
  } = skippedEnrollmentsResponse;
  const fetchMoreSkippedEnrollments = () => {
    fetchMore({
      variables: {
        offset
      }
    }).catch(err => {
      reportError({
        error: err
      });
    });
  };
  const openTroubleshootEnrollmentPanel = currentEnrollmentDetails => {
    if (currentEnrollmentDetails) {
      const {
        timestamp,
        objectId
      } = currentEnrollmentDetails;
      searchParams.set('objectId', objectId.toString());
      searchParams.set('timelineItem', `ENROLLMENT_LOG_EVENT-${timestamp.toString()}`);
    }
    searchParams.set('troubleshoot', 'true');
    history.replace({
      pathname: location.pathname,
      search: transformSearchParamsForTroubleshootEnrollment(searchParams)
    });
    trackEvent(Trackables.PERFORMANCE_HISTORY_TROUBLESHOOT_ENROLLMENT);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isTroubleshootEnrollmentPanelOpen && /*#__PURE__*/_jsx(NonEnrollmentDiagnosisPanelWrapper, {
      flowId: flow.flowId,
      flowObjectTypeId: flow.objectTypeId,
      onClose: () => {
        searchParams.delete('timelineItem');
        searchParams.delete('troubleshoot');
        history.replace({
          pathname: location.pathname,
          search: transformSearchParamsForEnrollmentHistory(searchParams)
        });
      }
    }), /*#__PURE__*/_jsx(SkippedEnrollmentsTable, {
      skippedEnrollmentsList: skippedEnrollmentsList,
      openTroubleshootEnrollmentPanel: openTroubleshootEnrollmentPanel
    }), hasMoreSkippedEnrollments && /*#__PURE__*/_jsx(View, {
      textAlign: "center",
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(UIButton, {
        onClick: () => {
          fetchMoreSkippedEnrollments();
        },
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.enrollmentHistory.panel.skipped-enrollments.table.loadMoreSkippedEnrollments"
        })
      })
    })]
  });
};
export default SkippedEnrollmentsContent;